import React, { useState, useEffect, useMemo } from 'react';
import { getAllProducts, createProduct, updateProduct, deleteProduct } from '../services/productService';
import {
  Banner,
  Button,
  Drawer,
  EmptyState,
  Field,
  FormSection,
  IconButton,
  Meter,
  Modal,
  Pagination,
  Pill,
  RowActions,
  SearchInput,
  SelectFilter,
  SkeletonRows,
  StatTiles,
  Tabs,
  Thumb,
  Toast,
  formatDate,
  formatRWF,
  useConfirm,
  useFlash,
  usePagination,
} from './AdminUI';
import { CATEGORY_GROUPS, buildCategoryTree, classifyCategory, isGeneralGroup, resolveShopCategory } from '../constants/shopCategories';
import ProductImageField from './ProductImageField';

const categoryLabel = (raw, title) => (raw ? classifyCategory(raw, title).subcategoryName : 'Uncategorised');
const categoryGroupLabel = (raw, title) => (raw ? classifyCategory(raw, title).categoryName : '');

/** Category for the product form: a saved general category (e.g. "Digital Security") becomes its sub-category when the name tells which. */
const formCategoryFor = (product) => {
  if (!resolveShopCategory(product.category)) return product.category || '';
  return classifyCategory(product.category, product.title).subcategoryName;
};

const EMPTY_FORM = {
  title: '',
  description: '',
  priceNew: '',
  priceOld: '',
  category: '',
  stock: '0',
  lowStockThreshold: '10'
};

const EMPTY_IMAGE = { file: null, url: '' };

const STOCK_META = {
  ok: { label: 'In stock', tone: 'green', meter: 'green' },
  low: { label: 'Low stock', tone: 'amber', meter: 'amber' },
  out: { label: 'Out of stock', tone: 'red', meter: 'red' }
};

const getStockState = (product) => {
  const stock = Number(product.stock) || 0;
  const threshold = Number(product.lowStockThreshold) || 10;
  if (stock <= 0) return 'out';
  if (stock <= threshold) return 'low';
  return 'ok';
};

const calculateDiscount = (priceOld, priceNew) => {
  if (!priceOld || !priceNew || parseFloat(priceOld) <= 0 || parseFloat(priceNew) <= 0) {
    return null;
  }
  const oldPrice = parseFloat(priceOld);
  const newPrice = parseFloat(priceNew);
  if (newPrice >= oldPrice) {
    return null;
  }
  const discountPercent = ((oldPrice - newPrice) / oldPrice) * 100;
  return Math.round(discountPercent * 100) / 100;
};

function ProductsManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useFlash();
  const [confirm, confirmDialog] = useConfirm();
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imageValue, setImageValue] = useState(EMPTY_IMAGE);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAllProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => buildCategoryTree(products), [products]);

  const categoryOptions = useMemo(
    () =>
      categories.flatMap((category) => [
        { value: category.id, label: category.name },
        ...category.subcategories
          .filter((sub) => !isGeneralGroup(sub))
          .map((sub) => ({ value: sub.id, label: `${category.name} › ${sub.name}` })),
      ]),
    [categories]
  );

  const counts = useMemo(() => {
    const result = { ok: 0, low: 0, out: 0, value: 0 };
    products.forEach((p) => {
      result[getStockState(p)] += 1;
      result.value += (Number(p.priceNew) || 0) * (Number(p.stock) || 0);
    });
    return result;
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = products.filter((p) => {
      if (stockFilter !== 'all' && getStockState(p) !== stockFilter) return false;
      if (categoryFilter !== 'all') {
        const { categoryId, subcategoryId } = classifyCategory(p.category, p.title);
        if (categoryId !== categoryFilter && subcategoryId !== categoryFilter) return false;
      }
      if (!q) return true;
      return [p.title, p.category, p.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
    const sorters = {
      newest: (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      az: (a, b) => (a.title || '').localeCompare(b.title || ''),
      'price-high': (a, b) => (Number(b.priceNew) || 0) - (Number(a.priceNew) || 0),
      'price-low': (a, b) => (Number(a.priceNew) || 0) - (Number(b.priceNew) || 0),
      'stock-low': (a, b) => (Number(a.stock) || 0) - (Number(b.stock) || 0)
    };
    return [...list].sort(sorters[sortBy]);
  }, [products, search, stockFilter, categoryFilter, sortBy]);

  const pager = usePagination(filtered, 10, `${search}|${stockFilter}|${categoryFilter}|${sortBy}`);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const imageFile = imageValue.file;
    const imageUrl = imageValue.url.trim();
    if (!imageFile && !imageUrl) {
      setFormError('Add a product image: upload a file or paste an image link.');
      return;
    }

    setSaving(true);
    try {
      const priceNew = parseFloat(formData.priceNew) || 0;
      const priceOld = formData.priceOld ? parseFloat(formData.priceOld) : null;

      // null clears a value that was removed while editing
      const productData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        priceNew,
        priceOld: priceOld || null,
        discount: calculateDiscount(priceOld, priceNew),
        category: formData.category,
        stock: parseInt(formData.stock, 10) || 0,
        lowStockThreshold: parseInt(formData.lowStockThreshold, 10) || 10
      };

      // A new file is uploaded to Cloudinary by the API; a link is only sent when it changed
      if (!imageFile && imageUrl !== (editingProduct?.image || '')) {
        productData.image = imageUrl;
      }

      const onProgress = imageFile ? setUploadProgress : undefined;
      if (imageFile) setUploadProgress(0);

      const productId = editingProduct?.id || editingProduct?._id;

      if (editingProduct && productId) {
        await updateProduct(productId, productData, imageFile, onProgress);
        setSuccess('Product updated');
      } else {
        await createProduct(productData, imageFile, onProgress);
        setSuccess('Product created');
      }

      closeModal();
      loadProducts();
    } catch (err) {
      setFormError(err.message || 'Failed to save product');
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  const handleEdit = (product) => {
    setViewingProduct(null);
    setEditingProduct(product);
    setFormError('');
    setFormData({
      title: product.title || '',
      description: product.description || '',
      priceNew: product.priceNew?.toString() || '0',
      priceOld: product.priceOld?.toString() || '',
      category: formCategoryFor(product),
      stock: product.stock?.toString() || '0',
      lowStockThreshold: product.lowStockThreshold?.toString() || '10'
    });
    setImageValue({ file: null, url: product.image || '' });
    setShowModal(true);
  };

  const handleDelete = (product) =>
    confirm({
      title: 'Delete this product?',
      message: 'It will be removed from the store immediately and customers will no longer be able to buy it.',
      item: {
        media: <Thumb src={product.image} />,
        title: product.title,
        meta: `${categoryLabel(product.category, product.title)} · ${formatRWF(product.priceNew)} · ${Number(product.stock) || 0} in stock`
      },
      points: ['This action cannot be undone.'],
      confirmLabel: 'Delete product',
      busyLabel: 'Deleting…',
      onConfirm: async () => {
        setSaving(true);
        setError('');
        try {
          await deleteProduct(product.id || product._id);
          setViewingProduct(null);
          setSuccess('Product deleted');
          loadProducts();
        } catch (err) {
          setError(err.message || 'Failed to delete product');
        } finally {
          setSaving(false);
        }
      }
    });

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData(EMPTY_FORM);
    setImageValue(EMPTY_IMAGE);
    setFormError('');
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData(EMPTY_FORM);
    setImageValue(EMPTY_IMAGE);
    setFormError('');
    setShowModal(true);
  };

  const stats = [
    { id: 'all', label: 'Total products', value: products.length, hint: `${categories.length} categories`, icon: 'fa-box', tone: 'blue', filter: 'all' },
    { id: 'ok', label: 'In stock', value: counts.ok, hint: `Stock value ${formatRWF(counts.value)}`, icon: 'fa-circle-check', tone: 'green', filter: 'ok' },
    { id: 'low', label: 'Low stock', value: counts.low, hint: 'Restock soon', icon: 'fa-battery-quarter', tone: 'amber', filter: 'low' },
    { id: 'out', label: 'Out of stock', value: counts.out, hint: 'Not purchasable', icon: 'fa-circle-xmark', tone: 'red', filter: 'out' }
  ];

  const discount = calculateDiscount(formData.priceOld, formData.priceNew);
  const hasFilters = search || stockFilter !== 'all' || categoryFilter !== 'all';

  return (
    <div className="ax">
      <StatTiles items={stats} active={stockFilter} onSelect={setStockFilter} />

      {error ? <Banner onClose={() => setError('')}>{error}</Banner> : null}

      <div className="ax-card">
        <div className="ax-toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search products…" label="Search products" />
          <SelectFilter
            value={categoryFilter}
            onChange={setCategoryFilter}
            icon="fa-layer-group"
            label="Category"
            options={[{ value: 'all', label: 'All categories' }, ...categoryOptions]}
          />
          <SelectFilter
            value={sortBy}
            onChange={setSortBy}
            icon="fa-arrow-down-wide-short"
            label="Sort products"
            options={[
              { value: 'newest', label: 'Newest first' },
              { value: 'az', label: 'Name A–Z' },
              { value: 'price-high', label: 'Price: high to low' },
              { value: 'price-low', label: 'Price: low to high' },
              { value: 'stock-low', label: 'Stock: lowest first' }
            ]}
          />
          <span className="ax-toolbar__grow" />
          <IconButton
            icon={loading ? 'fa-spinner fa-spin' : 'fa-rotate'}
            label="Refresh products"
            className="ax-icon-btn--outline"
            onClick={loadProducts}
            disabled={loading}
          />
          <Button variant="primary" icon="fa-plus" onClick={openCreateModal}>
            Add product
          </Button>
        </div>
        <div className="ax-toolbar ax-toolbar--sub">
          <Tabs
            value={stockFilter}
            onChange={setStockFilter}
            label="Stock status"
            options={[
              { id: 'all', label: 'All', count: products.length },
              { id: 'ok', label: 'In stock', count: counts.ok },
              { id: 'low', label: 'Low', count: counts.low },
              { id: 'out', label: 'Out', count: counts.out }
            ]}
          />
          {hasFilters ? (
            <Button
              variant="ghost"
              icon="fa-filter-circle-xmark"
              className="ax-btn--sm"
              onClick={() => {
                setSearch('');
                setStockFilter('all');
                setCategoryFilter('all');
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </div>

        {loading && !products.length ? (
          <SkeletonRows cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="fa-box-open"
            title={products.length ? 'No products match' : 'No products yet'}
            text={
              products.length
                ? 'Try a different search, category or stock filter.'
                : 'Add your first product to start building the Fabritech catalog.'
            }
            action={
              products.length ? null : (
                <Button variant="primary" icon="fa-plus" onClick={openCreateModal}>
                  Add product
                </Button>
              )
            }
          />
        ) : (
          <>
            <div className="ax-table-wrap">
              <table className="ax-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th className="is-right">Price</th>
                    <th style={{ width: 180 }}>Stock</th>
                    <th>Status</th>
                    <th className="is-right">
                      <span className="ax-sr">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pager.pageItems.map((product) => {
                    const state = getStockState(product);
                    const stock = Number(product.stock) || 0;
                    const threshold = Number(product.lowStockThreshold) || 10;
                    const onSale = product.priceOld && Number(product.priceOld) > Number(product.priceNew);
                    return (
                      <tr
                        key={product.id || product._id}
                        className="is-clickable"
                        onClick={() => setViewingProduct(product)}
                      >
                        <td>
                          <div className="ax-cell">
                            <Thumb src={product.image} />
                            <div className="ax-cell__text">
                              <strong>{product.title}</strong>
                              <small>Updated {formatDate(product.updatedAt || product.createdAt)}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="ax-tag" title={categoryGroupLabel(product.category, product.title)}>
                            {categoryLabel(product.category, product.title)}
                          </span>
                        </td>
                        <td className="is-right is-nowrap">
                          <span className="ax-strong">{formatRWF(product.priceNew)}</span>
                          {onSale ? (
                            <span className="ax-price-old">
                              <s>{formatRWF(product.priceOld)}</s>
                              <em>-{Math.round(calculateDiscount(product.priceOld, product.priceNew))}%</em>
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <div className="ax-stock">
                            <span className="ax-stock__num">
                              <strong>{stock}</strong> units
                            </span>
                            <Meter value={(stock / Math.max(threshold * 3, 1)) * 100} tone={STOCK_META[state].meter} />
                          </div>
                        </td>
                        <td>
                          <Pill tone={STOCK_META[state].tone}>{STOCK_META[state].label}</Pill>
                        </td>
                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                          <RowActions
                            label={`Actions for ${product.title}`}
                            title={product.title}
                            subtitle={`${categoryLabel(product.category, product.title)} · ${formatRWF(product.priceNew)} · ${stock} in stock`}
                            media={<Thumb src={product.image} />}
                            actions={[
                              {
                                id: 'view',
                                label: 'View details',
                                description: 'See images, pricing and description',
                                icon: 'fa-eye',
                                onClick: () => setViewingProduct(product)
                              },
                              {
                                id: 'edit',
                                label: 'Edit product',
                                description: 'Update title, price, image or stock',
                                icon: 'fa-pen',
                                onClick: () => handleEdit(product)
                              },
                              {
                                id: 'delete',
                                label: 'Delete product',
                                description: 'Remove it from the store permanently',
                                icon: 'fa-trash-can',
                                tone: 'danger',
                                disabled: saving,
                                onClick: () => handleDelete(product)
                              }
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination pager={pager} label="products" />
          </>
        )}
      </div>

      <Drawer
        open={Boolean(viewingProduct)}
        onClose={() => setViewingProduct(null)}
        title={viewingProduct?.title || ''}
        subtitle={viewingProduct ? `${categoryLabel(viewingProduct.category, viewingProduct.title)} · Added ${formatDate(viewingProduct.createdAt)}` : ''}
        footer={
          viewingProduct ? (
            <>
              <Button variant="danger" icon="fa-trash-can" disabled={saving} onClick={() => handleDelete(viewingProduct)}>
                Delete
              </Button>
              <span className="ax-foot__spacer" />
              <Button onClick={() => setViewingProduct(null)}>Close</Button>
              <Button variant="primary" icon="fa-pen" onClick={() => handleEdit(viewingProduct)}>
                Edit product
              </Button>
            </>
          ) : null
        }
      >
        {viewingProduct ? (
          <>
            <div className="ax-media">
              <Thumb src={viewingProduct.image} size="xl" />
              <div className="ax-media__tags">
                <Pill tone={STOCK_META[getStockState(viewingProduct)].tone}>
                  {STOCK_META[getStockState(viewingProduct)].label}
                </Pill>
              </div>
            </div>

            <div className="ax-kv-grid">
              <div className="ax-kv">
                <small>Price</small>
                <strong>{formatRWF(viewingProduct.priceNew)}</strong>
                {viewingProduct.priceOld ? <span className="ax-strike">{formatRWF(viewingProduct.priceOld)}</span> : null}
              </div>
              <div className="ax-kv">
                <small>Stock</small>
                <strong>{Number(viewingProduct.stock) || 0} units</strong>
                <span className="ax-sub">Alert at {Number(viewingProduct.lowStockThreshold) || 10}</span>
              </div>
              <div className="ax-kv">
                <small>Discount</small>
                <strong>
                  {calculateDiscount(viewingProduct.priceOld, viewingProduct.priceNew)
                    ? `${Math.round(calculateDiscount(viewingProduct.priceOld, viewingProduct.priceNew))}% off`
                    : 'None'}
                </strong>
              </div>
              <div className="ax-kv">
                <small>Stock value</small>
                <strong>{formatRWF((Number(viewingProduct.priceNew) || 0) * (Number(viewingProduct.stock) || 0))}</strong>
              </div>
            </div>

            <div className="ax-panel">
              <div className="ax-panel__head">
                <h3>
                  <i className="fas fa-align-left" aria-hidden="true" /> Description
                </h3>
              </div>
              <div className="ax-panel__body">
                <p className="ax-body-text">{viewingProduct.description || 'No description provided.'}</p>
              </div>
            </div>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={showModal}
        onClose={closeModal}
        as="form"
        onSubmit={handleSubmit}
        size="lg"
        icon={editingProduct ? 'fa-pen' : 'fa-box'}
        title={editingProduct ? 'Edit product' : 'New product'}
        subtitle={editingProduct ? editingProduct.title : 'Add an item to the Fabritech catalog'}
        footer={
          <>
            <Button onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" loading={saving} disabled={saving} icon="fa-check">
              {editingProduct ? 'Save changes' : 'Create product'}
            </Button>
          </>
        }
      >
        {formError ? <Banner>{formError}</Banner> : null}

        <FormSection title="Basic information" text="Shown on the product card and detail page.">
          <Field label="Product title" required span={2} htmlFor="pf-title">
            <input id="pf-title" className="ax-input" type="text" name="title" value={formData.title} onChange={handleInputChange} required placeholder="e.g. Starlink Standard Kit" />
          </Field>
          <Field label="Description" required span={2} htmlFor="pf-desc">
            <textarea id="pf-desc" className="ax-input" name="description" value={formData.description} onChange={handleInputChange} required rows="4" placeholder="Key features, what's in the box, warranty…" />
          </Field>
          <Field
            label="Category"
            required
            span={2}
            htmlFor="pf-cat"
            hint={
              formData.category && !resolveShopCategory(formData.category)
                ? 'This isn’t one of the shop categories — pick one so it’s listed correctly.'
                : 'Decides where the product is listed in the shop.'
            }
          >
            <select id="pf-cat" className="ax-input" name="category" value={formData.category} onChange={handleInputChange} required>
              <option value="" disabled>
                Select a category…
              </option>
              {CATEGORY_GROUPS.map((group) => (
                <optgroup key={group.id} label={group.name}>
                  {[...group.subcategories]
                    .sort((a, b) => isGeneralGroup(a) - isGeneralGroup(b))
                    .map((sub) => (
                      <option key={sub.id} value={sub.name}>
                        {isGeneralGroup(sub) && group.subcategories.length > 1 ? `Other ${group.name} products` : sub.name}
                      </option>
                    ))}
                </optgroup>
              ))}
              {formData.category && !resolveShopCategory(formData.category) ? (
                <option value={formData.category}>{formData.category} (other)</option>
              ) : null}
            </select>
          </Field>
        </FormSection>

        <FormSection title="Pricing" text="Add a compare-at price to show a discount.">
          <Field label="Selling price" required htmlFor="pf-price">
            <div className="ax-input-group">
              <span className="ax-input-group__addon">RWF</span>
              <input id="pf-price" className="ax-input" type="number" name="priceNew" value={formData.priceNew} onChange={handleInputChange} required min="0" step="0.01" placeholder="600000" />
            </div>
          </Field>
          <Field label="Compare-at price" htmlFor="pf-old">
            <div className="ax-input-group">
              <span className="ax-input-group__addon">RWF</span>
              <input id="pf-old" className="ax-input" type="number" name="priceOld" value={formData.priceOld} onChange={handleInputChange} min="0" step="0.01" placeholder="750000" />
            </div>
          </Field>
          {discount !== null ? (
            <div className="ax-callout">
              <i className="fas fa-tag" aria-hidden="true" />
              Customers will see a <strong>{discount}%</strong> discount.
            </div>
          ) : null}
        </FormSection>

        <FormSection title="Media" text="Shown on the shop card and product page. Uploaded images are stored on Cloudinary.">
          <ProductImageField
            value={imageValue}
            onChange={setImageValue}
            progress={uploadProgress}
            disabled={saving}
            required
          />
        </FormSection>

        <FormSection title="Inventory" text="Customers can only buy what is in stock.">
          <Field label="Stock quantity" required htmlFor="pf-stock">
            <input id="pf-stock" className="ax-input" type="number" name="stock" value={formData.stock} onChange={handleInputChange} required min="0" placeholder="0" />
          </Field>
          <Field label="Low-stock alert at" htmlFor="pf-threshold" hint="You'll be alerted when stock falls to this level.">
            <input id="pf-threshold" className="ax-input" type="number" name="lowStockThreshold" value={formData.lowStockThreshold} onChange={handleInputChange} min="0" placeholder="10" />
          </Field>
        </FormSection>
      </Modal>

      <Toast message={success} />
      {confirmDialog}
    </div>
  );
}

export default ProductsManagement;
